// Required imports
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ResponsivePie } from "@nivo/pie";
import { CompassParagraph } from "../../../components/common/styledComponents";
import {
  ElementContainer,
  ColumnContainer,
  Card,
} from "../../../components/containers/styledComponents";
import { LegendCircle } from "../../../components/visuals/styledComponents";
import { createCenteredValueLayer } from "./CenteredText";
import { generatePaletteColors } from "../../../utils/graphUtils";
import { darkGreen } from "../../../styles";
import {
  EngagementByTheme,
  StewardshipFilterParam,
} from "../../../models/stewardshipModels";
import { usePost } from "../../../hooks/usePost";
import { formatNumber, formatReadable } from "../../../utils/formattingUtils";
import { ExportButton } from "../../../components/buttons/ExportButton";
import { ENGAGEMENTS_BY_THEME_CONFIG } from "../constants";

const StyledChartContainer = styled.div<{ $compact: boolean }>`
  width: ${({ $compact }): string => ($compact ? "36%" : "60%")};
  height: ${({ $compact }): string => ($compact ? "100%" : "300px")};
  border-radius: 8px;
  overflow: visible;
  margin-bottom: ${({ $compact }): string => ($compact ? "0" : "20px")};
  margin-top: ${({ $compact }): string => ($compact ? "0" : "50px")};
`;

const LegendContainer = styled.div<{ paddingTop: number; $compact: boolean }>`
  display: flex;
  width: ${({ $compact }): string => ($compact ? "64%" : "auto")};
  height: ${({ $compact }): string => ($compact ? "100%" : "275px")};
  flex-direction: column;
  padding-top: ${({ paddingTop }): number => paddingTop}px;
  overflow-y: ${({ $compact }): string => ($compact ? "hidden" : "auto")};
  border-radius: 8px;
  margin-top: ${({ $compact }): string => ($compact ? "0" : "50px")};
`;

const CompactBody = styled.div`
  display: flex;
  min-height: 0;
  flex: 1;
  align-items: stretch;
`;

const CompactCard = styled(Card)`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const CompactLegendGrid = styled.div`
  display: grid;
  width: 100%;
  height: 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-content: center;
  gap: 6px 16px;

  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// The main component that renders the ResponsivePie chart
export const EngagementsByThemePie: React.FC<{
  filters: StewardshipFilterParam;
  compact?: boolean;
}> = ({ filters, compact = false }) => {
  const [pieData, setPieData] = useState<PieDatum[]>([]);
  const {
    data,
    isLoading,
    sendData: getEngagementsByTheme,
  } = usePost<EngagementByTheme[], StewardshipFilterParam>();
  const colors = generatePaletteColors(pieData);

  useEffect(() => {
    getEngagementsByTheme("GET_ENGAGEMENTS_BY_THEME", filters);
  }, [filters]);

  useEffect(() => {
    if (data) {
      const sortedData: EngagementByTheme[] = data.sort(
        (a, b) => b.engagement_count - a.engagement_count
      );
      const formattedData = sortedData.map((item, index) => ({
        id: item.theme,
        label: formatReadable(item.theme),
        value: formatNumber(item.engagement_count),
        color: colors[index % colors.length],
      }));
      setPieData(formattedData);
    }
  }, [data]);

  // Transforming data into a format that can be used by Nivo Pie
  const totalEngagements: string = data
    ? formatNumber(
        (data || []).reduce((sum, data) => sum + data.engagement_count, 0)
      )
    : "";
  // eslint-disable-next-line
  const customLayers: any = [
    createCenteredValueLayer(totalEngagements, true),
    createCenteredValueLayer(data ? "engagements" : "", false),
  ];

  const layersConfig = layers.concat(customLayers);

  const legendItems = pieData.map((item, index) => (
    <ElementContainer key={index} mb={compact ? 0 : 5}>
      <LegendCircle color={colors[index]}></LegendCircle>
      <CompassParagraph color="#8B909A" fontSize={12}>
        {item.label}: {item.value}
      </CompassParagraph>
    </ElementContainer>
  ));

  const chartAndLegend = (
    <>
      <StyledChartContainer $compact={compact}>
        <ResponsivePie
          theme={theme}
          data={pieData}
          margin={{ top: 10, right: 15, bottom: 15, left: 15 }}
          innerRadius={0.65}
          layers={layersConfig as any} //eslint-disable-line
          activeOuterRadiusOffset={8}
          borderWidth={1}
          colors={colors}
          borderColor={{ from: "color", modifiers: [["darker", 0]] }}
          arcLabelsSkipAngle={360}
          arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
          enableArcLinkLabels={false}
          defs={definitions}
          fill={fills}
          legends={[]}
        />
      </StyledChartContainer>
      <LegendContainer paddingTop={1} $compact={compact}>
        {compact ? (
          <CompactLegendGrid>{legendItems}</CompactLegendGrid>
        ) : (
          <ColumnContainer
            mt={1}
            jc={pieData.length > 8 ? "flex-start" : "center"}
            ai="flex-start"
            ml={0}
          >
            {legendItems}
          </ColumnContainer>
        )}
      </LegendContainer>
    </>
  );

  if (compact) {
    return (
      <CompactCard h="100%" m="0" isLoading={isLoading}>
        <CompassParagraph color={darkGreen} fontSize={16} bold={true}>
          Engagements by Theme
        </CompassParagraph>
        <CompactBody>{chartAndLegend}</CompactBody>
        {!isLoading && (
          <ExportButton data={pieData} {...ENGAGEMENTS_BY_THEME_CONFIG} />
        )}
      </CompactCard>
    );
  }

  return (
    <Card h="470px" isLoading={isLoading} style={{ position: "relative" }}>
      <CompassParagraph color={darkGreen} fontSize={16} bold={true}>
        Engagements by Theme
      </CompassParagraph>
      <ElementContainer ai="flex-start">{chartAndLegend}</ElementContainer>
      {!isLoading && (
        <ExportButton data={pieData} {...ENGAGEMENTS_BY_THEME_CONFIG} />
      )}
    </Card>
  );
};

// Configurations for the Nivo Pie chart
const theme = {
  legends: { text: { fontFamily: "Apercu", color: "greyText" } },
};

const layers = ["arcs", "arcLinkLabels", "legends"];

const definitions = [
  {
    id: "dots",
    type: "patternDots",
    background: "inherit",
    color: "rgba(255, 255, 255, 0.3)",
    size: 4,
    padding: 1,
    stagger: true,
  },
  {
    id: "lines",
    type: "patternLines",
    background: "inherit",
    color: "rgba(255, 255, 255, 0.3)",
    rotation: -45,
    lineWidth: 6,
    spacing: 10,
  },
];

const fills = [
  { match: { id: "dots" }, id: "dots" },
  { match: { id: "lines" }, id: "lines" },
];

export type PieDatum = {
  id: string | number;
  value: string;
  label?: string;
  color?: string;
};
